import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import orderBy from 'lodash.orderby';
import { toLocationOption } from '@helpers/locations';
import usePermissions from './usePermissions';
import { useTranslation } from './useTranslation';

export const useLocations = () => {
    const locations = useSelector(state => state.account?.account?.companies || []);
    const companiesCount = useSelector(state => state.account?.account?.companiesCount);
    const { t } = useTranslation();
    const noneGroup = `(${t('common.none')})`;
    const { getWritableLocationIds } = usePermissions();

    const locationsMap = useMemo(
        () =>
            locations.reduce((acc, location) => {
                acc[location.companyId] = location;
                return acc;
            }, {}),
        [locations],
    );

    const getLocationsOptions = filterCb => {
        const hasFilterCb = typeof filterCb === 'function';
        const filteredLocations = hasFilterCb ? locations.filter(filterCb) : locations;
        return orderBy(
            filteredLocations.map(item => toLocationOption(item, noneGroup)),
            ['group'],
            ['desc'],
        );
    };

    const getSelectLocationsOptions = filterCb => {
        const hasFilterCb = typeof filterCb === 'function';
        const filteredLocations = hasFilterCb ? locations.filter(filterCb) : locations;
        const groupedLocations = filteredLocations.reduce((acc, item) => {
            const groupName = item.brand ? item.brand.title : noneGroup;
            if (!acc[groupName]) {
                acc[groupName] = [];
            }
            acc[groupName].push({
                value: item.companyId,
                label: item.internalName,
            });
            return acc;
        }, {});

        return orderBy(
            Object.entries(groupedLocations).map(([group, children]) => ({
                group,
                children,
            })),
            ['group'],
            ['desc'],
        );
    };

    const getLocationsOptionsByBrand = brandId => {
        if (!brandId) return [];
        const filteredLocations = locations.filter(item => item.brand?.id === brandId);
        return orderBy(
            filteredLocations.map(item => toLocationOption(item, noneGroup)),
            ['group'],
            ['desc'],
        );
    };

    const mapSelectedLocationsToFormValue = selectedLocations => {
        if (!selectedLocations.length) return [];
        return selectedLocations
            .map(selectedLocation => locationsMap[selectedLocation])
            .filter(Boolean)
            .map(selectedCompany => toLocationOption(selectedCompany, noneGroup));
    };

    const mapLocationsToFormValue = filteredLocations =>
        (filteredLocations || locations).map(location => toLocationOption(location, noneGroup));

    const getWritableLocationsFormValue = useCallback(
        (module, filterCb) => {
            const writableIds = getWritableLocationIds(module);
            const writableFilter = loc => writableIds === null || writableIds.has(loc.companyId);
            const combinedFilter = filterCb ? loc => writableFilter(loc) && filterCb(loc) : writableFilter;
            return mapLocationsToFormValue(locations.filter(combinedFilter));
        },
        [getWritableLocationIds, locations, mapLocationsToFormValue],
    );

    const getWritableLocationsOptions = useCallback(
        (module, filterCb) => {
            const writableIds = getWritableLocationIds(module);
            const writableFilter = loc => writableIds === null || writableIds.has(loc.companyId);
            const combinedFilter = filterCb ? loc => writableFilter(loc) && filterCb(loc) : writableFilter;
            return getLocationsOptions(combinedFilter);
        },
        [getWritableLocationIds, getLocationsOptions],
    );

    const getWritableSelectLocationsOptions = useCallback(
        (module, filterCb) => {
            const writableIds = getWritableLocationIds(module);
            const writableFilter = loc => writableIds === null || writableIds.has(loc.companyId);
            const combinedFilter = filterCb ? loc => writableFilter(loc) && filterCb(loc) : writableFilter;
            return getSelectLocationsOptions(combinedFilter);
        },
        [getWritableLocationIds, getSelectLocationsOptions],
    );

    const hasSingleLocation = companiesCount === 1 && locations.length === 1;
    const singleLocation = hasSingleLocation ? locations[0] : null;
    const singleLocationId = singleLocation ? singleLocation.companyId : null;
    const singleLocationFormValue = useMemo(
        () => (singleLocationId ? mapSelectedLocationsToFormValue([singleLocationId]) : []),
        [singleLocationId, locationsMap],
    );

    return {
        mapLocationsToFormValue,
        mapSelectedLocationsToFormValue,
        getLocationsOptionsByBrand,
        getSelectLocationsOptions,
        getLocationsOptions,
        getWritableLocationsFormValue,
        getWritableLocationsOptions,
        getWritableSelectLocationsOptions,
        locationsMap,
        hasSingleLocation,
        singleLocation,
        singleLocationId,
        singleLocationFormValue,
    };
};
